import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { Session } from "../../features/sessions/models/Session";
import { SessionOccurrence } from "../../features/sessions/models/SessionOccurrence";
import { OccurrenceStatus } from "../../features/sessions/models/OccurrenceStatus";
import {
  fetchSessionOccurrences,
  rescheduleOccurrence,
} from "../../features/sessions/slice-extensions/occurrences-slice-extension";
import { formatDateDisplay } from "../../features/sessions/utils/DisplayMappers";
import Button from "../button/Button";

interface EditSingleOccurrencePanelProps {
  session: Session;
  /** Pre-selects the occurrence with this effective date (YYYY-MM-DD), e.g. the list row the edit started from. */
  initialDate?: string;
  onSaved: () => void;
}

const isoDate = (d: Date): string => d.toISOString().slice(0, 10);

/**
 * The "single occurrence" scope of the session editor: pick one date of the
 * series and move it to a new date/time. Everything else (title, roster,
 * recurrence) stays series-wide and is edited via the regular form.
 */
const EditSingleOccurrencePanel: React.FC<EditSingleOccurrencePanelProps> = ({
  session,
  initialDate,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [occurrences, setOccurrences] = useState<SessionOccurrence[] | null>(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectOccurrence = (occurrence: SessionOccurrence) => {
    setSelectedDate(occurrence.originalDate);
    setNewDate(occurrence.effectiveDate);
    setNewTime(occurrence.effectiveTime);
    setReason("");
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const from = new Date();
        from.setDate(from.getDate() - 31);
        const to = new Date();
        to.setDate(to.getDate() + 180);
        const { occurrences: fetched } = await dispatch(
          fetchSessionOccurrences({ bookingId: session.id, from: isoDate(from), to: isoDate(to) })
        ).unwrap();
        if (!active) return;
        const editable = fetched
          .filter((o) => o.status !== OccurrenceStatus.Cancelled)
          .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
        setOccurrences(editable);
        const today = isoDate(new Date());
        const preferred =
          editable.find((o) => o.effectiveDate === initialDate) ??
          editable.find((o) => o.effectiveDate >= today) ??
          editable[0];
        if (preferred) selectOccurrence(preferred);
      } catch {
        if (!active) return;
        setOccurrences([]);
        setError(t("sessions.editScope.occurrenceNotFound"));
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, session.id, initialDate]);

  const optionLabel = (o: SessionOccurrence): string => {
    const base = `${formatDateDisplay(o.effectiveDate)} · ${o.effectiveTime}`;
    if (o.status === OccurrenceStatus.Rescheduled) {
      return `${base} — ${t("sessions.occurrenceList.statusRescheduled")}`;
    }
    if (o.status === OccurrenceStatus.Retimed) {
      return `${base} — ${t("sessions.occurrenceList.statusRetimed")}`;
    }
    return base;
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    try {
      await dispatch(
        rescheduleOccurrence({
          bookingId: session.id,
          request: {
            occurrenceDate: selectedDate,
            newDate,
            newTime,
            reason: reason.trim() || undefined,
          },
        })
      ).unwrap();
      toast.success(t("sessions.occurrenceList.toastReschedule"));
      onSaved();
    } catch (e: unknown) {
      setError((e as { message?: string })?.message ?? t("sessions.occurrenceList.actionFailed"));
    } finally {
      setBusy(false);
    }
  };

  if (occurrences === null) {
    return <p className="text-sm text-grey-40 dark:text-grey-60">{t("sessions.sessionLoading")}</p>;
  }

  if (occurrences.length === 0) {
    return (
      <div className="with-border max-w-xl rounded-2xl p-4 lg:p-6">
        <p className="text-sm text-grey-30 dark:text-grey-70">
          {error ?? t("sessions.editScope.noUpcoming")}
        </p>
      </div>
    );
  }

  return (
    <div className="with-border max-w-xl space-y-4 rounded-2xl p-4 lg:p-6">
      <p className="text-sm text-grey-30 dark:text-grey-70">
        {t("sessions.editScope.occurrenceEditNote")}
      </p>

      <label className="block">
        <span className="text-sm text-grey-30 dark:text-grey-70">
          {t("sessions.editScope.occurrencePicker")}
        </span>
        <select
          className="input mt-1 w-full"
          value={selectedDate}
          disabled={busy}
          onChange={(e) => {
            const occurrence = occurrences.find((o) => o.originalDate === e.target.value);
            if (occurrence) selectOccurrence(occurrence);
          }}
        >
          {occurrences.map((o) => (
            <option key={o.originalDate} value={o.originalDate}>
              {optionLabel(o)}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm text-grey-30 dark:text-grey-70">
            {t("sessions.occurrenceList.newDate")}
          </span>
          <input
            type="date"
            className="input mt-1 w-full"
            value={newDate}
            disabled={busy}
            onChange={(e) => setNewDate(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm text-grey-30 dark:text-grey-70">
            {t("sessions.occurrenceList.newTime")}
          </span>
          <input
            type="time"
            className="input mt-1 w-full"
            value={newTime}
            disabled={busy}
            onChange={(e) => setNewTime(e.target.value)}
          />
        </label>
      </div>

      <label className="block">
        <span className="text-sm text-grey-30 dark:text-grey-70">
          {t("sessions.occurrenceList.reason")}
        </span>
        <input
          type="text"
          className="input mt-1 w-full"
          value={reason}
          maxLength={500}
          disabled={busy}
          placeholder={t("sessions.occurrenceList.reasonPlaceholder")}
          onChange={(e) => setReason(e.target.value)}
        />
      </label>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={busy || !newDate || !newTime}>
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
};

export default EditSingleOccurrencePanel;
